import { useMutation } from "@tanstack/react-query";
import {
  PaymentType,
  savePaymentOrderId,
} from "@/features/payment/utils";
import { useNavigate } from "@tanstack/react-router";
import { z } from "zod";
import { machineTypeSchema } from "./use-shop-details";
import { useRemovePayment } from "./use-remove-payment";
import { api } from "@/lib/server";
import { useMuiModal } from "@/components/modal";
import { ErrorModal } from "@/lib/api";
import { recordTrackedEvent } from "@/lib/kiosk-session-tracking";

export function useApplyPayment(options?: {
  /** After the failed payment was removed; defaults to the payment selector route. */
  onFailed?: (payment: PaymentType) => void;
}) {
  const navigate = useNavigate();
  const removePayment = useRemovePayment();
  const { showModal } = useMuiModal();

  return useMutation({
    mutationKey: ["apply-payment"],
    mutationFn: async ({
      payment,
      purchaseType,
    }: {
      payment: PaymentType;
      purchaseType:
        | "KIOSK_CASH"
        | "KIOSK_BANK_CARD"
        | "FULL_DISCOUNT"
        | "BALANCE";
    }) => {
      await recordTrackedEvent({
        eventType: "payment_started",
        kioskPaymentNonceKey: payment.kioskPaymentNonceKey,
        machineId: payment.machineId,
        dryerBundleId: payment.dryerBundleId,
        payload: { purchaseType },
      });

      const data = await api.applyPayment({
        purchaseType,
        kioskPaymentNonceKey: payment.kioskPaymentNonceKey,
      });
      if (data.type === "error") {
        showModal(
          <ErrorModal
            errorKey={data.error}
            errorDetails={data.errorDetails}
            translatedMessage={data.translatedMessage}
          />,
        );
        throw new Error(data.error);
      }
      if (data.data?.orderId) {
        savePaymentOrderId(payment, data.data.orderId);
      }
      // the old "Köszönjük" screen reads the collectors discount from here
      return data.data?.collectorsDiscount;
    },
    onError: (err, { payment }) => {
      removePayment.mutate({
        key: payment.kioskPaymentNonceKey,
        cause: "error",
        info: err.message,
      });
      if (options?.onFailed) {
        options.onFailed(payment);
        return;
      }
      navigate({
        to: "/payment-selector-screen/$machineId/{-$dryerBundleId}",
        params: { machineId: payment.machineId },
      });
    },
  });
}

export type CollectorsDiscountData = z.infer<typeof collectorsDiscountSchema>;
export const collectorsDiscountSchema = z.object({
  id: z.coerce.number(),
  collectorsDiscountCount: z.coerce.number(),
  neededTransactions: z.coerce.number(),
  couponCode: z.string().nullish(),
  discount: z.coerce.number().nullish(),
  discountType: z.enum(["fix", "percentage"]).nullish(),
  machineType: machineTypeSchema.array().nullish(),
  receivedTransactions: z.coerce.number().nullish(),
  couponExpiry: z
    .string()
    .transform((elem) => new Date(elem))
    .nullish(),
  couponCount: z.coerce.number().nullish(),
  xHead: z.string().nullish(),
  xBody: z.string().nullish(),
  yHead: z.string().nullish(),
  yBody: z.string().nullish(),
});

/**
 * apply-payment answers with the collectors discount (when there is one) and,
 * since the wash start guide, the purchase's order id. Older cash-agents send
 * no order id; KIOSK_CASH sends the cash payment state instead of a discount.
 */
export const applyPaymentResponseSchema = z
  .unknown()
  .transform((raw) => ({
    orderId: z
      .object({ orderId: z.string() })
      .safeParse(raw).data?.orderId,
    collectorsDiscount: collectorsDiscountSchema.safeParse(raw).data,
  }));
